import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader, ArrowRight } from "lucide-react";
import api from "../api/axios";

export default function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token");

    const [status, setStatus] = useState("verifying"); // verifying, success, error
    const [message, setMessage] = useState("Verifying your email...");

    const ranOnce = React.useRef(false);

    useEffect(() => {
        if (!token) {
            setStatus("error");
            setMessage("Invalid verification link.");
            return;
        }

        if (ranOnce.current) return;
        ranOnce.current = true;

        const verifyToken = async () => {
            try {
                await api.post("/auth/verify-email", { token });
                setStatus("success");
                setMessage("Email verified successfully! Welcome to the community.");

                // Optional: Auto redirect after 3s
            } catch (err) {
                setStatus("error");
                setMessage(err.response?.data?.error === "Invalid token"
                    ? "This link has expired or is invalid."
                    : "Verification failed. Please try again.");
            }
        };

        verifyToken();
    }, [token, navigate]);

    return (
        <div className="min-h-screen bg-white dark:bg-gray-950 flex flex-col items-center justify-center p-4">
            <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 text-center border border-gray-100 dark:border-gray-800">

                {status === "verifying" && (
                    <div className="flex flex-col items-center">
                        <Loader className="w-12 h-12 text-brand-blue animate-spin mb-4" />
                        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">Verifying...</h2>
                        <p className="text-gray-500">Please wait while we confirm your email.</p>
                    </div>
                )}

                {status === "success" && (
                    <div className="flex flex-col items-center animate-fadeIn">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Verified Successfully!</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-xs mx-auto">
                            Thank you for verifying your email. You can now close this tab and return to your original browser window to continue.
                        </p>

                        <p className="text-xs text-brand-blue font-medium bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-lg border border-blue-100 dark:border-blue-800">
                            Please return to the tab where you started signup.
                        </p>
                    </div>
                )}

                {status === "error" && (
                    <div className="flex flex-col items-center animate-fadeIn">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                            <XCircle className="w-8 h-8 text-red-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">Verification Failed</h2>
                        <p className="text-red-500 mb-6">{message}</p>

                        <Link
                            to="/dashboard"
                            className="text-gray-500 hover:text-gray-700 underline text-sm"
                        >
                            Back to Dashboard
                        </Link>
                    </div>
                )}

            </div>
        </div>
    );
}
